import { useField } from 'react-final-form';
import { useMemo } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import styles from './ErknmApproveRemarkChanges.module.scss';
import { ErknmApproveRemarkSectionCodeEnum } from '../enums/ErknmApproveRemarkSectionCodeEnum';
import { IKnmForm } from '../../../KnmPackage/MainModule/interfaces/IKnmForm';
import { ErknmApproveRemarkFieldCodeEnum } from '../enums/ErknmApproveRemarkFieldCodeEnum';
import { KnmHistoryOperationsEnum } from '../../../APIPackage/KnmModule/enums/KnmHistoryOperationsEnum';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import {
  getUserRoleIsProsecutor,
  getUserRoleIsSupervisor,
} from '../../../AuthPackage/MainModule/selectors/authSelectors';
import {
  getKnmIsErknmStatusCommentsExist,
  getKnmIsShowApproveRemarks,
} from '../../../KnmPackage/MainModule/selectors/knmSelectors';
import { getPlanListPlanErknmStatus } from '../../../PlanListPackage/MainModule/selectors/planListSelectors';
import { ErknmPlanStatusesEnum } from '../../../APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { getIsPlanErknmStatusConsidered } from '../../../APIPackage/PlanListModule/common/getPlanErknmStatusFlags';

const trimString = (value: string | null, length: number): string => {
  if (!value) {
    return '';
  }

  return value.length > length ? `${value.substring(0, length)}...` : value;
};

interface IErknmApproveRemarkChangesProps {
  sectionCode: ErknmApproveRemarkSectionCodeEnum;
  fieldCode?: ErknmApproveRemarkFieldCodeEnum;
  guid?: string | null;
  oldValue?: string;
  isLeftPosition?: boolean;
}

interface IErknmApproveRemarkChangesStoreProps {
  isSupervisor: boolean;
  isProsecutor: boolean;
  isKnmStatusCommentsExist: boolean;
  knmPlanStatus: ErknmPlanStatusesEnum | undefined;
  isShowApproveRemarks: boolean | undefined;
}

const ErknmApproveRemarkChangesWithoutStore = ({
  sectionCode,
  fieldCode,
  guid,
  oldValue,
  isSupervisor,
  isProsecutor,
  isKnmStatusCommentsExist,
  knmPlanStatus,
  isShowApproveRemarks,
  isLeftPosition,
}: IErknmApproveRemarkChangesProps & IErknmApproveRemarkChangesStoreProps) => {
  const approveRemarks: IKnmForm['approveRemarks'] = useField('approveRemarks' as keyof IKnmForm, {
    subscription: { value: true },
  }).input.value;

  const isPlanErknmStatusConsidered = getIsPlanErknmStatusConsidered(knmPlanStatus);
  const isShow =
    (isSupervisor && isKnmStatusCommentsExist && isPlanErknmStatusConsidered) || (isProsecutor && isShowApproveRemarks);

  const displayText = useMemo(() => {
    const changes = Array.isArray(approveRemarks)
      ? approveRemarks.find(item => item.sectionCode === sectionCode)?.changes
      : [];
    const fieldChanges = fieldCode ? changes?.filter(item => item.field === fieldCode) : [];
    const listItemChanges = guid ? changes?.filter(item => item.elementGuid === guid) : [];

    if (fieldChanges?.length) {
      const firstChange = fieldChanges[0];

      return `Значение поля изменено с "${trimString(firstChange.oldValue, 100)}" на "${trimString(
        oldValue || '',
        100,
      )}"`;
    }

    if (listItemChanges?.length) {
      const insertChange = listItemChanges.find(item => item.operation === KnmHistoryOperationsEnum.INSERT);
      const updateChange = listItemChanges.find(item => item.operation === KnmHistoryOperationsEnum.UPDATE);

      if (insertChange) {
        return 'Добавлена новая запись';
      }

      if (updateChange) {
        return 'Внесены изменения';
      }
    }
  }, [approveRemarks, fieldCode, guid, oldValue]);

  if (!displayText || !isShow) {
    return null;
  }

  return (
    <div className={styles.RemarkChanges}>
      <span className={styles.Icon}></span>
      <div className={styles.ChangesBlock}>
        <div className={classNames(styles.ChangesBlockContent, { [styles.LeftPosition]: isLeftPosition })}>
          {displayText}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: IRootState): IErknmApproveRemarkChangesStoreProps => ({
  isSupervisor: getUserRoleIsSupervisor(state),
  isProsecutor: getUserRoleIsProsecutor(state),
  isKnmStatusCommentsExist: getKnmIsErknmStatusCommentsExist(state),
  knmPlanStatus: getPlanListPlanErknmStatus(state),
  isShowApproveRemarks: getKnmIsShowApproveRemarks(state),
});

export const ErknmApproveRemarkChanges = connect(mapStateToProps)(ErknmApproveRemarkChangesWithoutStore);
